import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  useTheme,
  TablePagination,
} from '@mui/material';
import { 
  DatePicker, 
  LocalizationProvider 
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { 
  ArrowBackIos, 
  ArrowForwardIos,
  Refresh,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';

dayjs.locale('es');

const ContabilidadMedico = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [date, setDate] = useState(dayjs().tz('America/Costa_Rica'));
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5); // Fijo en 5 elementos por página
  const navigate = useNavigate();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const fetchData = useCallback(async (fecha) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/contabilidad/medico`, {
        params: { fecha: fecha.format('YYYY-MM-DD') },
        withCredentials: true
      });
      
      const formattedData = res.data.data.map((item, index) => ({
        ...item,
        id: index,
        costo_consulta: item.costo_consulta ? parseFloat(item.costo_consulta) : 0
      }));
      
      setData(formattedData);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  React.useEffect(() => {
    fetchData(date);
  }, [date, fetchData]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  const handlePreviousDay = () => {
    setDate(date.subtract(1, 'day'));
  };

  const handleNextDay = () => {
    setDate(date.add(1, 'day'));
  };


  // NUEVO: Exportar a Excel
  const exportToExcel = () => {
    const headers = [
      'Paciente',
      'Costo Consulta'
    ];
    const rows = data.map(item => [
      item.NombrePaciente,
      Number(item.costo_consulta)
    ]);
    // Fila de total
    rows.push([
      'Total',
      total
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 30 }, // Paciente
      { wch: 18 }, // Costo Consulta
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contabilidad Médico');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `Contabilidad_Medico_${date.format('YYYY_MM_DD')}.xlsx`);
  };

  return (
    <Box sx={{ 
        display: 'flex',
        padding: '64px 16px 16px 16px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        minHeight: 'calc(100vh - 64px)',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        margin: 0
    }}>
      <Paper
        elevation={3}
        sx={{
            padding: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            borderRadius: 2,
            margin: '0',
            boxSizing: 'border-box',
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            height: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginTop: '200px'
        }}
      >
        <Box sx={{ 
            display: 'flex',
            padding: '16px',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: 'auto',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            margin: 0,
            mb: 2
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main,
            mb: { xs: 2, sm: 0 }
          }}>
            Contabilidad Médico
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <IconButton 
            onClick={handlePreviousDay}
            color="primary"
            sx={{ 
              order: { xs: 0, sm: 0 },
              alignSelf: { xs: 'flex-start', sm: 'center' }
            }}
          >
            <ArrowBackIos />
          </IconButton>
          
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Seleccionar fecha"
              value={date}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              sx={{ 
                width: { xs: '100%', sm: 240 },
                backgroundColor: 'white',
                borderRadius: 1,
                order: { xs: 2, sm: 1 }
              }}
            />
          </LocalizationProvider>
          
          <IconButton 
            onClick={handleNextDay}
            color="primary"
            sx={{ 
              order: { xs: 1, sm: 2 },
              alignSelf: { xs: 'flex-end', sm: 'center' }
            }}
          >
            <ArrowForwardIos />
          </IconButton>
          
          <Button 
            variant="contained" 
            startIcon={<Refresh />} 
            onClick={() => fetchData(date)}
            sx={{ 
              order: 3,
              minWidth: 140,
              mt: { xs: 2, sm: 0 }
            }}
          >
            Actualizar
          </Button>
          <Button 
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<CalendarMonthIcon />}
            onClick={() => navigate('/contabilidad-mensual')}
            sx={{ 
              order: 4,
              minWidth: 140,
              mt: { xs: 2, sm: 0 }
            }}
          >
            Contabilidad Mensual
          </Button>
        </Box>
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            mb: 2,
            alignItems: 'center',
            width: '100%',
            order: { xs: 2, sm: 0 }
        }}>
            <Box sx={{ flex: '0 0 auto' }}> {/* Contenedor para el botón de Excel */}
                <Button 
                variant="contained" 
                color="success"
                startIcon={<DownloadIcon />} 
                onClick={exportToExcel}
                sx={{ minWidth: 180, height: 56, fontWeight: 'bold' }}
                disabled={data.length === 0}
                >
                Exportar a Excel
                </Button>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> {/* Contenedor para el total */}
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total del día:
                </Typography>
                <Typography variant="h5" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.success.main,
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: 1,
                    px: 2,
                    py: 1
                }}>
                    ₡{total.toFixed(2)}
                </Typography>
            </Box>
        </Box>

        <TableContainer 
          component={Paper} 
            sx={{ 
                maxHeight: '500px', // Reducir altura máxima
                width: '100%',
                overflowX: 'auto',
                overflowY: 'auto',
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '& .MuiTable-root': {
                    minWidth: 650 // Asegura un ancho mínimo
                }
            }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: theme.palette.primary.light,
                  color: 'white'
                }}>
                  Paciente
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: theme.palette.primary.light,
                  color: 'white',
                  textAlign: 'right'
                }}>
                  Costo Consulta
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Refresh sx={{ 
                        animation: 'spin 2s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        },
                        mr: 2
                      }} />
                      <Typography>Cargando datos...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Typography>No se encontraron registros para esta fecha</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.NombrePaciente}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: '500' }}>
                        ₡{parseFloat(row.costo_consulta).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            component="div"
            count={data.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]} // Solo permite 5 elementos por página
            labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            labelRowsPerPage="Filas por página:"
            />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 2,
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {data.length} registros encontrados
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Fecha: {date.format('dddd, D [de] MMMM [de] YYYY')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContabilidadMedico;